/**
 * Catalogo declarativo de TODOS os widgets do dashboard configuravel
 * (Bloco VG). Single source of truth — adicionar widget eh adicionar
 * um arquivo em widgets/ e 1 entry aqui.
 *
 * Cada widget exporta:
 *   meta       = {id, label, descricao, categoria, permissoes, tamanho_default, essencial?}
 *   fetchData  = (request) => Promise<dados>     // null se nao faz fetch
 *   Widget     = ({ dados, loading, error, contexto }) => JSX
 *   visivelPara= (hasAllPermissions, isMaster) => bool   // opcional; gate dinamico
 *
 * O `index.jsx` da pagina monta o grid a partir de `WIDGETS` apos
 * filtrar por permissoes do usuario e por preferencia salva no backend.
 */

// Sistema (universal — sem permissao)
import * as BoasVindas from "./widgets/BoasVindasWidget";
import * as AtalhosRapidos from "./widgets/AtalhosRapidosWidget";

// TOC
import * as Throughput from "./widgets/ThroughputWidget";
import * as Gargalo from "./widgets/GargaloWidget";
import * as Wip from "./widgets/WipWidget";
import * as Buffer from "./widgets/BufferWidget";

// Financeiro
import * as SaldoTotal from "./widgets/SaldoTotalWidget";
import * as EntradasSaidasMes from "./widgets/EntradasSaidasMesWidget";
import * as ProjecaoFluxo from "./widgets/ProjecaoFluxo4SemWidget";
import * as DreResumo from "./widgets/DreResumoWidget";
import * as RentabilidadeTop from "./widgets/RentabilidadeTopWidget";

// Comercial
import * as Pipeline from "./widgets/PipelineWidget";
import * as TaxaConversao from "./widgets/TaxaConversaoWidget";
import * as TicketMedio from "./widgets/TicketMedioWidget";
import * as Funil from "./widgets/FunilWidget";
import * as VendedoresTop from "./widgets/VendedoresTopWidget";

// Pendencias acionaveis
import * as ContratosPendentes from "./widgets/ContratosPendentesWidget";
import * as OsesAtrasadas from "./widgets/OsesAtrasadasWidget";
import * as AssistenciasAbertas from "./widgets/AssistenciasAbertasWidget";
// Solicitações internas (PM-07 / §8.8): W1–W3.
import * as SolicitacoesAguardando from "./widgets/SolicitacoesAguardandoWidget";
import * as SolicitacoesBacklog from "./widgets/SolicitacoesBacklogWidget";
import * as RequisicoesSeparar from "./widgets/RequisicoesSepararWidget";
import * as ProdutosPendentes from "./widgets/ProdutosPendentesWidget";
import * as CargasEmRota from "./widgets/CargasEmRotaWidget";
import * as OrcamentosVencendo from "./widgets/OrcamentosVencendoWidget";

// Producao
import * as OpsSituacao from "./widgets/OpsSituacaoWidget";
import * as OpsCriadasHoje from "./widgets/OpsCriadasHojeWidget";

// Chat Pharu (o chat interno; o WhatsApp abaixo é o atendimento de clientes)
import * as ChatNaoLidas from "./widgets/ChatNaoLidasWidget";

// WhatsApp
import * as MensagensNaoLidas from "./widgets/MensagensNaoLidasWidget";
import * as InstanciasConectadas from "./widgets/InstanciasConectadasWidget";


// Ordem aqui = ordem default de aparicao no painel "Personalizar"
// (agrupada visualmente por categoria).
const RAW = [
  // Sistema
  BoasVindas,
  AtalhosRapidos,
  // TOC
  Throughput,
  Gargalo,
  Wip,
  Buffer,
  // Financeiro
  SaldoTotal,
  EntradasSaidasMes,
  ProjecaoFluxo,
  DreResumo,
  RentabilidadeTop,
  // Comercial
  Pipeline,
  TaxaConversao,
  TicketMedio,
  Funil,
  VendedoresTop,
  // Pendencias
  ContratosPendentes,
  OsesAtrasadas,
  AssistenciasAbertas,
  SolicitacoesAguardando,
  SolicitacoesBacklog,
  RequisicoesSeparar,
  ProdutosPendentes,
  CargasEmRota,
  OrcamentosVencendo,
  // Producao
  OpsSituacao,
  OpsCriadasHoje,
  // Chat Pharu
  ChatNaoLidas,
  // WhatsApp
  MensagensNaoLidas,
  InstanciasConectadas,
];


export const WIDGETS = RAW.map((mod) => ({
  ...mod.meta,
  fetchData: mod.fetchData ?? null,
  Componente: mod.Widget,
  // Visibilidade dinamica opcional (hasAllPermissions, isMaster) => bool.
  // Ex.: AtalhosRapidos some quando nenhum atalho passa no gate de rota.
  visivelPara: mod.visivelPara ?? null,
}));


export const WIDGETS_POR_ID = WIDGETS.reduce((acc, w) => {
  acc[w.id] = w;
  return acc;
}, {});


/**
 * Filtra widgets do catalogo pelos que o usuario tem permissao de ver.
 * Usuario sem nenhuma permissao listada no widget eh bloqueado.
 * Widget com `permissoes: []` eh universal (sempre incluido).
 *
 * @param hasAllPermissions  funcao do AuthContext
 * @param isMaster           bool — Master ve TUDO (bypass)
 */
export function filtrarPorPermissoes(hasAllPermissions, isMaster = false) {
  return WIDGETS.filter((w) => {
    const permitido =
      !w.permissoes ||
      w.permissoes.length === 0 ||
      isMaster ||
      hasAllPermissions(w.permissoes);
    if (!permitido) return false;
    // Gate dinamico do widget (ex.: AtalhosRapidos sem nenhum atalho
    // acessivel nao renderiza card vazio — some do grid e do Personalizar).
    if (typeof w.visivelPara === "function" && !w.visivelPara(hasAllPermissions, isMaster)) {
      return false;
    }
    return true;
  });
}


/**
 * Monta o "default essencial" de widgets para um usuario novo
 * (sem preferencia salva). Inclui apenas widgets que:
 *   - tem `essencial: true` no meta
 *   - o usuario tem permissao
 *
 * Retorna lista no formato salvo em `PreferenciaDashboard.widgets`:
 *   [{id, posicao, tamanho}]
 */
export function defaultEssencial(hasAllPermissions, isMaster = false) {
  const disponiveis = filtrarPorPermissoes(hasAllPermissions, isMaster);
  return disponiveis
    .filter((w) => w.essencial)
    .map((w, idx) => ({
      id: w.id,
      posicao: idx,
      tamanho: w.tamanho_default || "M",
    }));
}


/**
 * Categorias unicas (para agrupar no "Personalizar").
 */
export function categorias() {
  return Array.from(new Set(WIDGETS.map((w) => w.categoria || "Outros")));
}
